/* ORION — Collection Marquee v5.0
   Infinite autoscroll with drag/swipe support.
   Uses CSS transform: translateX() instead of scrollLeft for iOS Safari reliability.

   Strategy:
   - Auto-scroll runs via rAF, advancing a translateX offset on a track wrapper
   - CSS transforms are GPU-composited and NOT throttled by iOS Safari
   - Touch handling uses ONLY touch events (no pointer events on mobile)
   - Mouse drag uses pointer events (desktop only)
   - A watchdog timer guarantees auto-scroll always resumes
   - Dawn's SliderComponent scroll listener is neutralized
*/

(function () {
  const SELECTOR = 'slider-component[data-orion-marquee="true"]';
  const instances = new Map();

  function destroyMarquee(root) {
    const inst = instances.get(root);
    if (!inst) return;
    if (inst.stop) inst.stop();
    if (inst.ro) inst.ro.disconnect();
    const slider = inst.slider;
    const track = inst.track;
    if (slider && track) {
      // Unwrap: move children back from track to slider
      while (track.firstChild) {
        slider.appendChild(track.firstChild);
      }
      track.remove();
      // Remove clones
      slider.querySelectorAll('[data-orion-clone="true"]').forEach((n) => n.remove());
      // Restore slider styles
      slider.style.overflow = '';
      slider.style.display = '';
    } else if (slider) {
      slider.querySelectorAll('[data-orion-clone="true"]').forEach((n) => n.remove());
    }
    root.__orionMarquee = false;
    instances.delete(root);
  }

  function initMarquee(root) {
    const slider = root.querySelector('[id^="Slider-"]');
    if (!slider) return;

    if (root.__orionMarquee) {
      destroyMarquee(root);
    }

    if (root.offsetParent === null) return;

    // --- Disable Dawn's SliderComponent scroll/button behavior for this element ---
    if (typeof root.update === 'function') {
      root.nextButton = null;
      root.prevButton = null;
      root.slider = null;
    }

    // UX: prevent text selection and tap highlights while dragging
    root.style.webkitTouchCallout = 'none';
    root.style.webkitUserSelect = 'none';
    root.style.userSelect = 'none';
    root.style.webkitTapHighlightColor = 'transparent';
    root.style.touchAction = 'pan-y';
    slider.style.touchAction = 'pan-y';
    slider.style.scrollSnapType = 'none';
    slider.style.scrollBehavior = 'auto';

    root.querySelectorAll('input, textarea, button, select').forEach((el) => {
      el.style.webkitUserSelect = 'auto';
      el.style.userSelect = 'auto';
    });

    // --- Clone slides for infinite loop ---
    slider.querySelectorAll('[data-orion-clone="true"]').forEach((n) => n.remove());
    // Remove any leftover track wrapper from previous init
    slider.querySelectorAll('[data-orion-track]').forEach((n) => {
      while (n.firstChild) slider.appendChild(n.firstChild);
      n.remove();
    });

    const getDirectBaseSlides = () => Array.from(slider.querySelectorAll(':scope > li:not([data-orion-clone="true"])'));

    const baseSlides = getDirectBaseSlides();
    if (baseSlides.length < 2) return;

    // --- Measure slide width BEFORE wrapping (Dawn grid CSS is still active) ---
    const measuredSlideWidth = baseSlides[0].offsetWidth;
    const measuredGap = parseFloat(getComputedStyle(slider).columnGap) || 0;
    if (measuredSlideWidth <= 0) return;

    // --- Clone slides ---
    const fragStart = document.createDocumentFragment();
    const fragEnd = document.createDocumentFragment();

    baseSlides.forEach((s) => {
      const c1 = s.cloneNode(true);
      c1.setAttribute('data-orion-clone', 'true');
      c1.removeAttribute('id');
      fragEnd.appendChild(c1);

      const c2 = s.cloneNode(true);
      c2.setAttribute('data-orion-clone', 'true');
      c2.removeAttribute('id');
      fragStart.appendChild(c2);
    });

    slider.insertBefore(fragStart, slider.firstChild);
    slider.appendChild(fragEnd);

    const baseCount = baseSlides.length;

    // --- Create track wrapper ---
    const track = document.createElement('div');
    track.setAttribute('data-orion-track', 'true');
    track.style.display = 'flex';
    track.style.flexWrap = 'nowrap';
    track.style.willChange = 'transform';
    track.style.columnGap = measuredGap + 'px';

    // Move all li children into the track
    while (slider.firstChild) {
      track.appendChild(slider.firstChild);
    }
    slider.appendChild(track);

    // --- Override ul to be a clipping viewport ---
    slider.style.overflow = 'hidden';
    slider.style.display = 'block';
    slider.style.overflowY = 'hidden';

    // --- Lock slide widths (% values break when parent changes) ---
    function setSlideWidths(w) {
      track.querySelectorAll(':scope > li').forEach((li) => {
        li.style.width = w + 'px';
        li.style.minWidth = w + 'px';
        li.style.flexShrink = '0';
      });
    }
    setSlideWidths(measuredSlideWidth);

    // --- Helpers that query from the track ---
    const getSlides = () => Array.from(track.querySelectorAll(':scope > li'));

    const getBaseWidth = () => {
      const slidesNow = getSlides();
      const startEl = slidesNow[baseCount];
      const endEl = slidesNow[baseCount + baseCount - 1];
      if (!startEl || !endEl) return 0;
      return endEl.offsetLeft + endEl.offsetWidth - startEl.offsetLeft;
    };

    let baseWidth = getBaseWidth();
    if (!baseWidth) {
      // Cleanup on failure
      while (track.firstChild) slider.appendChild(track.firstChild);
      track.remove();
      slider.querySelectorAll('[data-orion-clone="true"]').forEach((n) => n.remove());
      slider.style.overflow = '';
      slider.style.display = '';
      return;
    }

    root.__orionMarquee = true;

    // --- Position at middle chunk via transform ---
    const getMiddleStart = () => {
      const slidesNow = getSlides();
      return slidesNow[baseCount] ? slidesNow[baseCount].offsetLeft : 0;
    };

    let offset = -getMiddleStart();
    track.style.transform = 'translateX(' + offset + 'px)';

    // Speed: px per frame at 60fps
    const speedAttr = parseFloat(root.getAttribute('data-orion-marquee-speed') || '0.6');
    const baseSpeed = Number.isFinite(speedAttr) ? speedAttr : 0.6;

    // --- State ---
    let raf = null;
    let lastTs = null;
    let isStopped = false;
    let userInteracting = false;
    let isHovered = false;
    let watchdogTimer = null;
    let velocity = 0;
    let currentSpeed = baseSpeed;
    let isBlending = false;

    // Center slide tracking (mobile)
    let currentCenterSlide = null;

    function normalizeLoop() {
      const bw = getBaseWidth();
      if (bw > 0) baseWidth = bw;

      const slidesNow = getSlides();
      const middleEl = slidesNow[baseCount];
      if (!middleEl) return;

      const leftEdge = -middleEl.offsetLeft;
      const rightEdge = leftEdge - baseWidth;

      if (offset <= rightEdge + 2) offset += baseWidth;
      else if (offset >= leftEdge - 2) offset -= baseWidth;

      track.style.transform = 'translateX(' + offset + 'px)';
    }

    function updateCenterSlide() {
      if (window.innerWidth > 749) return;

      const slides = track.querySelectorAll(':scope > li');
      if (!slides.length) return;

      const sliderRect = slider.getBoundingClientRect();
      const sliderCenter = sliderRect.left + sliderRect.width / 2;

      let closestSlide = null;
      let closestDistance = Infinity;

      slides.forEach((slide) => {
        const slideRect = slide.getBoundingClientRect();
        const slideCenter = slideRect.left + slideRect.width / 2;
        const distance = Math.abs(slideCenter - sliderCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestSlide = slide;
        }
      });

      if (closestSlide && closestSlide !== currentCenterSlide) {
        if (currentCenterSlide) currentCenterSlide.classList.remove('is-center');
        closestSlide.classList.add('is-center');
        currentCenterSlide = closestSlide;
      }
    }

    function tick(ts) {
      if (isStopped) { raf = null; return; }

      if (lastTs == null) lastTs = ts;
      const dt = Math.min(ts - lastTs, 100);
      lastTs = ts;

      if (!userInteracting && !isHovered) {
        if (isBlending) {
          currentSpeed += (baseSpeed - currentSpeed) * 0.08;
          if (Math.abs(currentSpeed - baseSpeed) < 0.01) {
            isBlending = false;
            currentSpeed = baseSpeed;
          }
        }

        offset -= currentSpeed * (dt / 16.67);
        track.style.transform = 'translateX(' + offset + 'px)';
        normalizeLoop();
      }

      updateCenterSlide();

      raf = requestAnimationFrame(tick);
    }

    function start() {
      isStopped = false;
      if (raf) cancelAnimationFrame(raf);
      lastTs = null;
      raf = requestAnimationFrame(tick);
    }

    function stop() {
      isStopped = true;
      if (raf) { cancelAnimationFrame(raf); raf = null; }
    }

    function startBlendToBase(fromVelocity) {
      currentSpeed = -fromVelocity * 16;
      currentSpeed = Math.max(-50, Math.min(50, currentSpeed));
      isBlending = true;
    }

    function armWatchdog() {
      clearWatchdog();
      watchdogTimer = setTimeout(() => {
        userInteracting = false;
      }, 5000);
    }

    function clearWatchdog() {
      if (watchdogTimer) { clearTimeout(watchdogTimer); watchdogTimer = null; }
    }

    function navigateFromPoint(clientX, clientY) {
      const el = document.elementFromPoint(clientX, clientY);
      const a = el && el.closest ? el.closest('a') : null;
      if (a && a.href) {
        if (a.target === '_blank') {
          window.open(a.href, '_blank', 'noopener');
        } else {
          window.location.href = a.href;
        }
      }
    }

    // Recalculate layout (used on resize and tab switch)
    function recalc() {
      const viewportWidth = slider.clientWidth;
      if (viewportWidth <= 0) return;

      // Determine column count from CSS classes
      const w = window.innerWidth;
      let cols = 4;
      if (w >= 990) {
        const match = slider.className.match(/grid--(\d+)-col-desktop/);
        if (match) cols = parseInt(match[1], 10);
      } else {
        const match = slider.className.match(/grid--(\d+)-col-tablet-down/);
        if (match) cols = parseInt(match[1], 10);
      }

      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const newSlideWidth = (viewportWidth - gap * (cols - 1)) / cols;
      if (newSlideWidth > 0) {
        setSlideWidths(newSlideWidth);
      }

      baseWidth = getBaseWidth();
      if (baseWidth > 0) {
        const slidesNow = getSlides();
        const middleEl = slidesNow[baseCount];
        if (middleEl) {
          offset = -middleEl.offsetLeft;
          track.style.transform = 'translateX(' + offset + 'px)';
        }
        normalizeLoop();
      }
    }

    // ==================== DESKTOP: mouse hover + pointer drag ====================
    root.addEventListener('mouseenter', () => { isHovered = true; }, { passive: true });
    root.addEventListener('mouseleave', () => { isHovered = false; }, { passive: true });

    let ptrDown = false;
    let ptrStartX = 0;
    let ptrStartY = 0;
    let ptrStartOffset = 0;
    let ptrDidDrag = false;
    let ptrDirection = null;
    let ptrLastX = 0;
    let ptrLastTs = 0;
    const DRAG_THRESHOLD = 8;

    slider.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') return;
      if (e.button !== 0) return;

      ptrDown = true;
      ptrDidDrag = false;
      ptrDirection = null;
      userInteracting = true;
      isBlending = false;
      currentSpeed = baseSpeed;

      slider.setPointerCapture(e.pointerId);

      ptrStartX = e.clientX;
      ptrStartY = e.clientY;
      ptrStartOffset = offset;
      ptrLastX = e.clientX;
      ptrLastTs = performance.now();
      velocity = 0;
    }, { passive: true });

    slider.addEventListener('pointermove', (e) => {
      if (!ptrDown || e.pointerType === 'touch') return;

      const dx = e.clientX - ptrStartX;
      const dy = e.clientY - ptrStartY;

      if (!ptrDirection && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
        ptrDirection = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
      }

      if (ptrDirection === 'v') {
        try { slider.releasePointerCapture(e.pointerId); } catch (_) {}
        ptrDown = false;
        userInteracting = false;
        return;
      }

      if (Math.abs(dx) > 10) ptrDidDrag = true;

      if (ptrDirection === 'h') {
        offset = ptrStartOffset + dx;
        track.style.transform = 'translateX(' + offset + 'px)';
        normalizeLoop();
      }

      const now = performance.now();
      const dt = now - ptrLastTs;
      if (dt > 0) {
        velocity = velocity * 0.7 + ((e.clientX - ptrLastX) / dt) * 0.3;
        ptrLastTs = now;
        ptrLastX = e.clientX;
      }
    }, { passive: true });

    slider.addEventListener('pointerup', (e) => {
      if (!ptrDown || e.pointerType === 'touch') return;
      ptrDown = false;
      userInteracting = false;
      try { slider.releasePointerCapture(e.pointerId); } catch (_) {}

      if (!ptrDidDrag) {
        navigateFromPoint(e.clientX, e.clientY);
      } else {
        startBlendToBase(velocity);
      }
      ptrDirection = null;
    });

    slider.addEventListener('pointercancel', (e) => {
      if (!ptrDown || e.pointerType === 'touch') return;
      ptrDown = false;
      userInteracting = false;
      ptrDirection = null;
    });

    // ==================== MOBILE: touch events only ====================
    let tActive = false;
    let tStartX = 0;
    let tStartY = 0;
    let tStartOffset = 0;
    let tLastX = 0;
    let tLastTs = 0;
    let tDirection = null;
    let tDidDrag = false;

    slider.addEventListener('touchstart', (e) => {
      if (!e.touches || e.touches.length !== 1) return;

      tActive = true;
      tDidDrag = false;
      tDirection = null;
      userInteracting = true;
      isBlending = false;
      currentSpeed = baseSpeed;
      armWatchdog();

      const t = e.touches[0];
      tStartX = t.clientX;
      tStartY = t.clientY;
      tStartOffset = offset;
      tLastX = t.clientX;
      tLastTs = performance.now();
      velocity = 0;
    }, { passive: true });

    slider.addEventListener('touchmove', (e) => {
      if (!tActive || !e.touches || e.touches.length !== 1) return;

      const t = e.touches[0];
      const dx = t.clientX - tStartX;
      const dy = t.clientY - tStartY;

      if (!tDirection && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
        tDirection = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
      }

      if (tDirection === 'v') {
        tActive = false;
        userInteracting = false;
        clearWatchdog();
        return;
      }

      if (tDirection === 'h' && e.cancelable) {
        e.preventDefault();
      }

      if (Math.abs(dx) > 18) tDidDrag = true;

      if (tDirection === 'h') {
        offset = tStartOffset + dx;
        track.style.transform = 'translateX(' + offset + 'px)';
        normalizeLoop();
      }

      const now = performance.now();
      const dt = now - tLastTs;
      if (dt > 0) {
        velocity = velocity * 0.7 + ((t.clientX - tLastX) / dt) * 0.3;
        tLastTs = now;
        tLastX = t.clientX;
      }
    }, { passive: false });

    slider.addEventListener('touchend', (e) => {
      if (!tActive) return;
      tActive = false;
      userInteracting = false;
      clearWatchdog();

      if (!tDidDrag) {
        const changed = e.changedTouches && e.changedTouches[0];
        if (changed) navigateFromPoint(changed.clientX, changed.clientY);
      } else {
        startBlendToBase(velocity);
      }
      tDirection = null;
    }, { passive: true });

    slider.addEventListener('touchcancel', () => {
      tActive = false;
      userInteracting = false;
      clearWatchdog();
      tDirection = null;
    }, { passive: true });

    // ==================== WHEEL ====================
    let wheelTimeout = null;
    slider.addEventListener('wheel', () => {
      isHovered = true;
      clearTimeout(wheelTimeout);
      wheelTimeout = setTimeout(() => { isHovered = false; }, 300);
    }, { passive: true });

    // ==================== RESIZE ====================
    const ro = new ResizeObserver(() => {
      recalc();
    });
    ro.observe(slider);

    // Prevent native image drag
    slider.addEventListener('dragstart', (e) => e.preventDefault());

    // Store instance
    instances.set(root, { slider, track, ro, stop, start, recalc });

    start();
  }

  function boot() {
    document.querySelectorAll(SELECTOR).forEach(initMarquee);

    // Tab switch: stop/start RAF
    const mo = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        if (m.type !== 'attributes' || m.attributeName !== 'class') return;
        const el = m.target;
        if (!el.matches || !el.matches(SELECTOR)) return;

        const inst = instances.get(el);
        if (el.classList.contains('hidden')) {
          if (inst && inst.stop) inst.stop();
        } else {
          if (inst) {
            requestAnimationFrame(() => {
              inst.recalc();
              inst.start();
            });
          } else {
            requestAnimationFrame(() => initMarquee(el));
          }
        }
      });
    });

    document.querySelectorAll(SELECTOR).forEach((el) => {
      mo.observe(el, { attributes: true });
    });

    // Shopify theme editor
    document.addEventListener('shopify:section:load', () => {
      document.querySelectorAll(SELECTOR).forEach(initMarquee);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
